import { useQuery, useQueryClient } from '@tanstack/react-query';
import { motion, AnimatePresence } from 'framer-motion';
import {
  History,
  Search,
  ShieldAlert,
  Laptop,
  ChevronDown,
  ChevronUp,
  Filter,
  Download,
  Trash2,
  Calendar,
  User,
  Shield,
  X,
} from 'lucide-react';
import { useState, Fragment } from 'react';

import { Badge, EmptyState, Spinner } from '@terra/ui';

import { api } from '../lib/api';

export function AuditLogsPage() {
  const [searchDraft, setSearchDraft] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

  // Advanced Filters
  const [showFilters, setShowFilters] = useState(false);
  const [actorId, setActorId] = useState('');
  const [actorRole, setActorRole] = useState('');
  const [moduleName, setModuleName] = useState('');
  const [actionType, setActionType] = useState('');
  const [ipAddress, setIpAddress] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const [activeFilters, setActiveFilters] = useState({
    actorId: '',
    actorRole: '',
    moduleName: '',
    actionType: '',
    ipAddress: '',
    startDate: '',
    endDate: '',
  });

  // Retention Pruning Modal
  const [showPruneModal, setShowPruneModal] = useState(false);
  const [pruneDays, setPruneDays] = useState(90);
  const [isPruning, setIsPruning] = useState(false);

  const queryClient = useQueryClient();

  const { data: response, isLoading } = useQuery({
    queryKey: ['audit-logs', search, page, activeFilters],
    queryFn: () => {
      const params: any = { page, pageSize: 15 };
      if (search) params.q = search;
      if (activeFilters.actorId) params.actorId = activeFilters.actorId;
      if (activeFilters.actorRole) params.actorRole = activeFilters.actorRole;
      if (activeFilters.moduleName) params.module = activeFilters.moduleName;
      if (activeFilters.actionType) params.action = activeFilters.actionType;
      if (activeFilters.ipAddress) params.ip = activeFilters.ipAddress;
      if (activeFilters.startDate) params.startDate = activeFilters.startDate;
      if (activeFilters.endDate) params.endDate = activeFilters.endDate;
      return api.platform.listAuditLogs(params);
    },
  });

  const logs = response?.data || [];
  const pageInfo = response?.page;

  const handleSearch = () => {
    setSearch(searchDraft.trim());
    setActiveFilters({
      actorId: actorId.trim(),
      actorRole,
      moduleName: moduleName.trim(),
      actionType: actionType.trim(),
      ipAddress: ipAddress.trim(),
      startDate,
      endDate,
    });
    setPage(1);
  };

  const handleResetFilters = () => {
    setSearchDraft('');
    setSearch('');
    setActorId('');
    setActorRole('');
    setModuleName('');
    setActionType('');
    setIpAddress('');
    setStartDate('');
    setEndDate('');
    setActiveFilters({
      actorId: '',
      actorRole: '',
      moduleName: '',
      actionType: '',
      ipAddress: '',
      startDate: '',
      endDate: '',
    });
    setPage(1);
  };

  const handleExportCsv = async () => {
    try {
      const csvResponse = await api.http.get('/admin/platform/audit-logs', {
        params: {
          q: search || undefined,
          actorId: activeFilters.actorId || undefined,
          actorRole: activeFilters.actorRole || undefined,
          module: activeFilters.moduleName || undefined,
          action: activeFilters.actionType || undefined,
          ip: activeFilters.ipAddress || undefined,
          startDate: activeFilters.startDate || undefined,
          endDate: activeFilters.endDate || undefined,
          exportCsv: true,
        },
        responseType: 'blob',
      });
      const blob = new Blob([csvResponse.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `audit-logs-${new Date().toISOString().slice(0, 10)}.csv`);
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      console.error('Failed to export CSV', err);
      alert(err?.message || 'Failed to export CSV');
    }
  };

  const handlePruneLogs = async (e: React.FormEvent) => {
    e.preventDefault();
    if (pruneDays < 0) {
      alert('Retention days must be a positive number');
      return;
    }
    const confirmed = window.confirm(
      `Are you sure you want to permanently prune audit logs older than ${pruneDays} days? This action is irreversible.`,
    );
    if (!confirmed) return;

    setIsPruning(true);
    try {
      const res = await api.platform.pruneAuditLogs(pruneDays);
      alert(`Successfully pruned ${res.prunedCount} audit log entries.`);
      setShowPruneModal(false);
      void queryClient.invalidateQueries({ queryKey: ['audit-logs'] });
    } catch (err: any) {
      console.error(err);
      alert(err?.message || 'Failed to prune audit logs');
    } finally {
      setIsPruning(false);
    }
  };

  const toggleRow = (id: string) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const getRoleBadgeTone = (role: string | null) => {
    if (!role) return 'neutral' as const;
    const r = role.toUpperCase();
    if (r.includes('SUPER_ADMIN')) return 'danger' as const;
    if (r.includes('ADMIN')) return 'warning' as const;
    if (r.includes('VENDOR')) return 'info' as const;
    return 'neutral' as const;
  };

  const getActionColor = (action: string) => {
    if (
      action.includes('delete') ||
      action.includes('remove') ||
      action.includes('suspend') ||
      action.includes('reject')
    ) {
      return 'text-red-650 bg-red-50/80 border-red-200 dark:bg-red-950/20';
    }
    if (
      action.includes('approve') ||
      action.includes('create') ||
      action.includes('register') ||
      action.includes('write')
    ) {
      return 'text-emerald-750 bg-emerald-50/80 border-emerald-205 dark:bg-emerald-950/20';
    }
    if (action.includes('update') || action.includes('edit')) {
      return 'text-amber-750 bg-amber-50/80 border-amber-200 dark:bg-amber-950/20';
    }
    return 'text-blue-750 bg-blue-50/80 border-blue-200 dark:bg-blue-950/20';
  };

  return (
    <div className="space-y-6 pb-8">
      {/* Search and filter toolbar */}
      <div className="border-border/60 bg-surface overflow-hidden rounded-2xl border shadow-sm">
        <div className="border-border/60 bg-surface-subtle/30 flex flex-col flex-wrap items-center justify-between gap-4 border-b px-5 py-4 lg:flex-row">
          <div className="flex items-center gap-3">
            <div className="bg-surface-subtle text-ink-muted ring-border/60 grid h-8 w-8 place-items-center rounded-lg ring-1">
              <History className="h-4 w-4" strokeWidth={2} />
            </div>
            <span className="text-ink text-[14px] font-bold">System Audit Logs</span>
          </div>

          <div className="flex w-full flex-wrap items-center gap-2 lg:w-auto">
            <div className="relative w-full sm:w-60">
              <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                <Search className="text-ink-muted h-4 w-4" strokeWidth={2} />
              </div>
              <input
                type="text"
                placeholder="Search action, actor, entity..."
                value={searchDraft}
                onChange={(e) => setSearchDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleSearch();
                  }
                }}
                className="border-border/60 bg-surface-subtle/50 text-ink focus:border-gold-400 focus:bg-surface focus:ring-gold-400 placeholder:text-ink-subtle h-9 w-full rounded-xl border pl-9 pr-3 text-[12px] font-medium transition-colors focus:outline-none focus:ring-1"
              />
            </div>
            <button
              onClick={handleSearch}
              className="bg-ink hover:bg-ink/90 flex h-9 shrink-0 items-center justify-center rounded-xl px-4 text-[12px] font-bold text-white shadow-sm transition-all"
            >
              Search
            </button>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`border-border/60 hover:bg-surface-subtle flex h-9 shrink-0 items-center gap-1.5 rounded-xl border px-3 text-[12px] font-bold transition-all ${
                showFilters
                  ? 'bg-gold-500/10 border-gold-400 text-gold-600 dark:text-gold-400'
                  : 'bg-surface text-ink-muted'
              }`}
            >
              <Filter className="h-3.5 w-3.5" />
              <span>Filters</span>
            </button>
            <button
              onClick={handleExportCsv}
              className="border-border/60 bg-surface hover:bg-surface-subtle text-ink-muted hover:text-ink flex h-9 shrink-0 items-center gap-1.5 rounded-xl border px-3 text-[12px] font-bold transition-all"
            >
              <Download className="h-3.5 w-3.5" />
              <span>Export CSV</span>
            </button>
            <button
              onClick={() => setShowPruneModal(true)}
              className="border-border/60 bg-surface hover:bg-surface-subtle flex h-9 shrink-0 items-center gap-1.5 rounded-xl border px-3 text-[12px] font-bold text-red-500 transition-all hover:border-red-300 hover:text-red-600"
            >
              <Trash2 className="h-3.5 w-3.5" />
              <span>Prune Logs</span>
            </button>
          </div>
        </div>

        {/* Collapsible filters panel */}
        <AnimatePresence>
          {showFilters && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.2 }}
              className="border-border/60 bg-surface overflow-hidden border-b px-5 py-4"
            >
              <div className="grid gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
                <div>
                  <label className="text-ink-muted mb-1 block text-[11px] font-bold uppercase tracking-wider">
                    Actor ID
                  </label>
                  <div className="relative">
                    <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                      <User className="text-ink-muted h-3.5 w-3.5" />
                    </div>
                    <input
                      type="text"
                      placeholder="Enter User ID..."
                      value={actorId}
                      onChange={(e) => setActorId(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                      className="border-border/60 bg-surface-subtle/50 text-ink focus:border-gold-400 focus:bg-surface focus:ring-gold-400 placeholder:text-ink-subtle h-9 w-full rounded-xl border pl-9 pr-3 text-[12px] font-medium transition-colors focus:outline-none focus:ring-1"
                    />
                  </div>
                </div>

                <div>
                  <label className="text-ink-muted mb-1 block text-[11px] font-bold uppercase tracking-wider">
                    Actor Role
                  </label>
                  <div className="relative">
                    <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                      <Shield className="text-ink-muted h-3.5 w-3.5" />
                    </div>
                    <select
                      value={actorRole}
                      onChange={(e) => setActorRole(e.target.value)}
                      className="border-border/60 bg-surface-subtle/50 text-ink focus:border-gold-400 focus:bg-surface focus:ring-gold-400 placeholder:text-ink-subtle h-9 w-full appearance-none rounded-xl border pl-9 pr-3 text-[12px] font-medium transition-colors focus:outline-none focus:ring-1"
                    >
                      <option value="">All Roles</option>
                      <option value="SUPER_ADMIN">SUPER_ADMIN</option>
                      <option value="ADMIN_STAFF">ADMIN_STAFF</option>
                      <option value="VENDOR">VENDOR</option>
                      <option value="CUSTOMER">CUSTOMER</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="text-ink-muted mb-1 block text-[11px] font-bold uppercase tracking-wider">
                    Module
                  </label>
                  <input
                    type="text"
                    placeholder="e.g. settings, catalog"
                    value={moduleName}
                    onChange={(e) => setModuleName(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                    className="border-border/60 bg-surface-subtle/50 text-ink focus:border-gold-400 focus:bg-surface focus:ring-gold-400 placeholder:text-ink-subtle h-9 w-full rounded-xl border px-3 text-[12px] font-medium transition-colors focus:outline-none focus:ring-1"
                  />
                </div>

                <div>
                  <label className="text-ink-muted mb-1 block text-[11px] font-bold uppercase tracking-wider">
                    Action Type
                  </label>
                  <input
                    type="text"
                    placeholder="e.g. settings.update"
                    value={actionType}
                    onChange={(e) => setActionType(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                    className="border-border/60 bg-surface-subtle/50 text-ink focus:border-gold-400 focus:bg-surface focus:ring-gold-400 placeholder:text-ink-subtle h-9 w-full rounded-xl border px-3 text-[12px] font-medium transition-colors focus:outline-none focus:ring-1"
                  />
                </div>

                <div>
                  <label className="text-ink-muted mb-1 block text-[11px] font-bold uppercase tracking-wider">
                    IP Address
                  </label>
                  <input
                    type="text"
                    placeholder="e.g. 127.0.0.1"
                    value={ipAddress}
                    onChange={(e) => setIpAddress(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                    className="border-border/60 bg-surface-subtle/50 text-ink focus:border-gold-400 focus:bg-surface focus:ring-gold-400 placeholder:text-ink-subtle h-9 w-full rounded-xl border px-3 text-[12px] font-medium transition-colors focus:outline-none focus:ring-1"
                  />
                </div>

                <div>
                  <label className="text-ink-muted mb-1 block text-[11px] font-bold uppercase tracking-wider">
                    Start Date
                  </label>
                  <div className="relative">
                    <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                      <Calendar className="text-ink-muted h-3.5 w-3.5" />
                    </div>
                    <input
                      type="date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      className="border-border/60 bg-surface-subtle/50 text-ink focus:border-gold-400 focus:bg-surface focus:ring-gold-400 placeholder:text-ink-subtle h-9 w-full rounded-xl border pl-9 pr-3 text-[12px] font-medium transition-colors focus:outline-none focus:ring-1"
                    />
                  </div>
                </div>

                <div>
                  <label className="text-ink-muted mb-1 block text-[11px] font-bold uppercase tracking-wider">
                    End Date
                  </label>
                  <div className="relative">
                    <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                      <Calendar className="text-ink-muted h-3.5 w-3.5" />
                    </div>
                    <input
                      type="date"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                      className="border-border/60 bg-surface-subtle/50 text-ink focus:border-gold-400 focus:bg-surface focus:ring-gold-400 placeholder:text-ink-subtle h-9 w-full rounded-xl border pl-9 pr-3 text-[12px] font-medium transition-colors focus:outline-none focus:ring-1"
                    />
                  </div>
                </div>

                <div className="flex items-end justify-end gap-2 sm:col-span-2 md:col-span-3 lg:col-span-1">
                  <button
                    onClick={handleResetFilters}
                    className="border-border/60 hover:bg-surface-subtle text-ink-muted flex h-9 items-center justify-center rounded-xl border px-4 text-[12px] font-bold transition-all"
                  >
                    Reset
                  </button>
                  <button
                    onClick={handleSearch}
                    className="bg-gold-500 hover:bg-gold-600 flex h-9 items-center justify-center rounded-xl px-4 text-[12px] font-bold text-white shadow-sm transition-all"
                  >
                    Apply
                  </button>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        <div className="p-0">
          {isLoading ? (
            <div className="grid min-h-[300px] place-items-center">
              <Spinner size="lg" />
            </div>
          ) : logs.length === 0 ? (
            <div className="border-border/60 bg-surface-subtle/30 m-5 rounded-2xl border border-dashed py-12">
              <EmptyState
                icon={<ShieldAlert className="text-ink-muted h-10 w-10" />}
                title="No audit logs found"
                description={
                  search
                    ? 'Try reframing your search query.'
                    : 'Audit log history is currently empty.'
                }
              />
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead>
                  <tr className="border-border/60 text-ink-muted bg-surface/50 border-b text-[10px] font-bold uppercase tracking-wider">
                    <th className="w-10 py-3 pl-5"></th>
                    <th className="py-3 pr-4">Timestamp</th>
                    <th className="py-3 pr-4">Actor</th>
                    <th className="py-3 pr-4">Action</th>
                    <th className="py-3 pr-4">Target Entity</th>
                    <th className="py-3 pr-4">Network Info</th>
                  </tr>
                </thead>
                <tbody className="divide-border/40 divide-y text-[13px]">
                  {logs.map((row) => {
                    const isExpanded = expandedRows.has(row.id);
                    const hasDetails = row.before !== null || row.after !== null;
                    return (
                      <Fragment key={row.id}>
                        <tr
                          className={`transition-colors ${
                            hasDetails
                              ? 'hover:bg-surface-subtle/40 cursor-pointer'
                              : 'hover:bg-surface-subtle/10'
                          }`}
                          onClick={() => hasDetails && toggleRow(row.id)}
                        >
                          <td className="py-3 pl-5 text-center">
                            {hasDetails ? (
                              isExpanded ? (
                                <ChevronUp className="text-ink-muted h-4 w-4" />
                              ) : (
                                <ChevronDown className="text-ink-muted h-4 w-4" />
                              )
                            ) : null}
                          </td>
                          <td className="text-ink-muted whitespace-nowrap py-3 pr-4 font-medium">
                            {new Date(row.createdAt).toLocaleString()}
                          </td>
                          <td className="py-3 pr-4">
                            <div className="flex flex-col gap-0.5">
                              {row.actor ? (
                                <>
                                  <span className="text-ink font-semibold">{row.actor.name}</span>
                                  <span className="text-ink-muted text-[11px]">
                                    {row.actor.email}
                                  </span>
                                </>
                              ) : (
                                <span className="text-ink-muted font-medium italic">
                                  System Automated
                                </span>
                              )}
                              {row.actorRole && (
                                <div className="mt-1">
                                  <Badge
                                    tone={getRoleBadgeTone(row.actorRole)}
                                    className="px-1.5 py-0 text-[10px]"
                                  >
                                    {row.actorRole}
                                  </Badge>
                                </div>
                              )}
                            </div>
                          </td>
                          <td className="py-3 pr-4">
                            <span
                              className={`inline-flex items-center rounded-lg border px-2.5 py-0.5 font-mono text-[12px] font-bold ${getActionColor(
                                row.action,
                              )}`}
                            >
                              {row.action}
                            </span>
                          </td>
                          <td className="py-3 pr-4">
                            {row.entityType ? (
                              <div className="space-y-0.5">
                                <div className="text-ink font-semibold">{row.entityType}</div>
                                {row.entityId && (
                                  <div
                                    className="text-ink-muted max-w-[150px] truncate font-mono text-[11px]"
                                    title={row.entityId}
                                  >
                                    ID: {row.entityId}
                                  </div>
                                )}
                              </div>
                            ) : (
                              <span className="text-ink-subtle">—</span>
                            )}
                          </td>
                          <td className="whitespace-nowrap py-3 pr-4">
                            <div className="text-ink-muted flex flex-col gap-0.5 text-[12px]">
                              {row.ip && (
                                <div className="flex items-center gap-1">
                                  <Laptop className="h-3.5 w-3.5" />
                                  <span>{row.ip}</span>
                                </div>
                              )}
                              {row.requestId && (
                                <span className="font-mono text-[10px]">
                                  Req: {row.requestId.substring(0, 8)}...
                                </span>
                              )}
                            </div>
                          </td>
                        </tr>
                        {isExpanded && hasDetails && (
                          <tr>
                            <td colSpan={6} className="bg-surface-subtle/20 px-8 py-4">
                              <motion.div
                                initial={{ opacity: 0, height: 0 }}
                                animate={{ opacity: 1, height: 'auto' }}
                                exit={{ opacity: 0, height: 0 }}
                                className="grid gap-6 overflow-hidden md:grid-cols-2"
                              >
                                <div>
                                  <div className="text-ink-muted mb-2 text-[11px] font-bold uppercase tracking-wider">
                                    Before State
                                  </div>
                                  <pre className="border-border/40 bg-surface text-ink max-h-72 overflow-x-auto rounded-xl border p-4 font-mono text-[12px] shadow-inner">
                                    {row.before ? JSON.stringify(row.before, null, 2) : 'null'}
                                  </pre>
                                </div>
                                <div>
                                  <div className="text-ink-muted mb-2 text-[11px] font-bold uppercase tracking-wider">
                                    After State
                                  </div>
                                  <pre className="border-border/40 bg-surface text-ink max-h-72 overflow-x-auto rounded-xl border p-4 font-mono text-[12px] shadow-inner">
                                    {row.after ? JSON.stringify(row.after, null, 2) : 'null'}
                                  </pre>
                                </div>
                              </motion.div>
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {pageInfo && pageInfo.pageCount > 1 && (
            <div className="border-border/60 flex items-center justify-center gap-2 border-t py-4">
              <button
                type="button"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                className="border-border/60 bg-surface text-ink-muted hover:bg-surface-subtle flex h-9 w-9 items-center justify-center rounded-xl border transition-colors disabled:opacity-40"
              >
                &larr;
              </button>
              {Array.from({ length: pageInfo.pageCount }, (_, i) => i + 1).map((n) => (
                <button
                  key={n}
                  type="button"
                  onClick={() => setPage(n)}
                  className={`flex h-9 w-9 items-center justify-center rounded-xl text-[12px] font-bold transition-colors ${
                    n === page
                      ? 'bg-gold-500 border-gold-500 border text-white shadow-sm'
                      : 'border-border/60 bg-surface text-ink-muted hover:bg-surface-subtle hover:text-ink border'
                  }`}
                >
                  {n}
                </button>
              ))}
              <button
                type="button"
                disabled={page >= pageInfo.pageCount}
                onClick={() => setPage((p) => Math.min(pageInfo.pageCount, p + 1))}
                className="border-border/60 bg-surface text-ink-muted hover:bg-surface-subtle flex h-9 w-9 items-center justify-center rounded-xl border transition-colors disabled:opacity-40"
              >
                &rarr;
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Pruning modal */}
      <AnimatePresence>
        {showPruneModal && (
          <Modal
            title="Prune Audit Logs (Retention Policy)"
            onClose={() => setShowPruneModal(false)}
          >
            <form onSubmit={handlePruneLogs} className="space-y-4">
              <div>
                <p className="text-ink-muted mb-4 text-[13px] leading-relaxed">
                  Permanently delete older system audit logs to conserve storage space. This action
                  uses the{' '}
                  <code className="bg-surface-subtle rounded border px-1 py-0.5 font-mono text-[11px]">
                    platform.settings.write
                  </code>{' '}
                  permission.
                </p>
                <label className="text-ink mb-1.5 block text-[13px] font-bold">
                  Retention Window (Days to Keep)
                </label>
                <div className="relative">
                  <input
                    type="number"
                    min="0"
                    required
                    value={pruneDays}
                    onChange={(e) => setPruneDays(parseInt(e.target.value) || 0)}
                    className="border-border/60 bg-surface-subtle/50 text-ink focus:border-gold-400 focus:bg-surface focus:ring-gold-400 h-10 w-full rounded-xl border px-3 text-[13px] font-semibold transition-colors focus:outline-none focus:ring-1"
                  />
                </div>
                <p className="text-ink-subtle mt-2 text-[11px]">
                  All audit logs generated more than <strong>{pruneDays}</strong> days ago will be
                  deleted permanently.
                </p>
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setShowPruneModal(false)}
                  className="border-border/60 text-ink-muted hover:text-ink h-9 rounded-xl border px-4 text-[13px] font-bold transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isPruning}
                  className="flex h-9 items-center gap-2 rounded-xl bg-red-600 px-5 text-[13px] font-bold text-white shadow-sm hover:bg-red-700 disabled:opacity-50"
                >
                  {isPruning ? <Spinner size="sm" className="animate-spin text-white" /> : null}
                  Confirm Prune
                </button>
              </div>
            </form>
          </Modal>
        )}
      </AnimatePresence>
    </div>
  );
}

// Reusable Modal Component
function Modal({
  title,
  onClose,
  children,
}: {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
}) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 grid place-items-center bg-black/40 p-4 backdrop-blur-sm"
      onClick={onClose}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 10 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 10 }}
        transition={{ duration: 0.15 }}
        className="border-border/60 bg-surface w-full max-w-md overflow-hidden rounded-2xl border shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="border-border/60 flex items-center justify-between border-b px-5 py-4">
          <h3 className="text-ink text-[15px] font-bold">{title}</h3>
          <button
            onClick={onClose}
            className="text-ink-muted hover:text-ink hover:bg-surface-subtle grid h-8 w-8 place-items-center rounded-lg transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="p-5">{children}</div>
      </motion.div>
    </motion.div>
  );
}
